import { ReactNode, useEffect } from 'react';

import {
	OidcConfiguration,
	OidcProvider,
	OidcSecure,
	useOidc,
	useOidcIdToken,
} from '@axa-fr/react-oidc';

import {
	Authenticating,
	AuthenticatingError,
	CallbackSuccess,
	Loading,
	ServiceWorkerNotSupported,
	SessionLost,
} from '@repo/ui/messages/authMessages';

import { OidcIdToken } from './user.types';

export const AuthProvider = ({
	config,
	children,
}: {
	config: OidcConfiguration;
	children: ReactNode;
}) => (
	<OidcProvider
		loadingComponent={Loading}
		authenticatingComponent={Authenticating}
		serviceWorkerNotSupportedComponent={ServiceWorkerNotSupported}
		authenticatingErrorComponent={AuthenticatingError}
		sessionLostComponent={SessionLost}
		callbackSuccessComponent={CallbackSuccess}
		configuration={config}
	>
		<OidcSecure extras={{ sso: 'False' }}>
			<EnforceClient clientId={config.client_id}>{children}</EnforceClient>
		</OidcSecure>
	</OidcProvider>
);

const EnforceClient = ({
	clientId,
	children,
}: {
	clientId: string;
	children: ReactNode;
}) => {
	const { idTokenPayload } = useOidcIdToken() as OidcIdToken;
	const { logout } = useOidc();
	const hasAudienceMismatch =
		idTokenPayload != null && idTokenPayload.aud !== clientId;

	useEffect(() => {
		if (hasAudienceMismatch) {
			console.error(
				`OIDC token audience (${idTokenPayload.aud}) does not match expected client ID (${clientId})`,
			);
			void logout();
		}
	}, [hasAudienceMismatch, logout, clientId, idTokenPayload?.aud]);

	if (hasAudienceMismatch) {
		return <SessionLost />;
	}

	return children;
};
